import React, { useState, useEffect } from 'react';
import Box from '@material-ui/core/Box';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Typography from '@mui/material/Typography';
import storeIcon from '../../assets/SVGs/Vector.svg';
import {
	NEED_TO_BE_SELECTED,
	states,
	NETSALES,
	UNITS,
	GMV,
	StoreChannel,
	OmniChannel,
	eComChannel,
	channelOptionMapping,
	BOGLE_LIGHT,
	locationArr,
	OMNI,
} from '../../constants/constant';
import OutlinedInput from '@mui/material/OutlinedInput';
import { useStyles, styles, selectStyleMenu } from './styles';
import ListItemText from '@mui/material/ListItemText';
import Checkbox from '@mui/material/Checkbox';
import PropTypes from 'prop-types';
import AddItem from './AddItems/AddItem';
import { Tooltip, HotSpot } from './FT-UX';
import { getStoredTooltipData } from './FT-UX/util';
import { getKeyByValue } from '../../helper/commonUtils';

const getLocation = (arr, key) => {
	return arr.find((obj) => {
		return obj.name === key;
	});
};

const getCB = (metricDomain) => {
	if (metricDomain === 'SALES') {
		return OMNI;
	}
	return 'Store';
};

const Location = ({ metric, metricDomain, getLocationData, editRuleData }) => {
	const cbKey = getKeyByValue(channelOptionMapping, editRuleData?.channelType);
	const editLocation = getLocation(locationArr, editRuleData?.location);
	const classes = useStyles();
	const [location, setLocation] = useState(
		editRuleData ? editLocation?.name : 'USA'
	);
	const [channelOptions, setChannelOptions] = useState(
		editRuleData ? editRuleData?.channels : []
	);
	const [stateOptions, setStateOptions] = useState(
		editRuleData ? editRuleData?.locationFilters : []
	);
	const [addStore, setAddStore] = useState(false);
	const [cb, setCB] = useState(editRuleData ? cbKey : getCB(metricDomain));
	const [storeList, setStoreList] = useState({});
	const storedValue = getStoredTooltipData();

	const getExecutionType = () => {
		if (storeList?.alertForIndividual) {
			return 'INDIVIDUAL';
		} else {
			return 'AGGREGATE';
		}
	};

	useEffect(() => {
		setChannelOptions([]);
		setStateOptions([]);
	}, [location]);

	useEffect(() => {
		const data = {
			location,
			channels: channelOptions,
			locationFilters:
				location === 'STORES' ? storeList?.selectedItems : stateOptions,
			channelType: channelOptionMapping[cb],
			executionTypeForLocation:
				location === 'STORES' ? getExecutionType() : 'AGGREGATE',
		};
		getLocationData(data);
	}, [location, channelOptions, stateOptions, cb, storeList]);

	useEffect(() => {
		if (cbKey !== cb) {
			setLocation('USA');
			setChannelOptions([]);
			setStateOptions([]);
		} else {
			setLocation(editRuleData ? editLocation?.name : 'USA');
			setChannelOptions(editRuleData ? editRuleData?.channels : []);
			setStateOptions(editRuleData ? editRuleData?.locationFilters : []);
		}
	}, [cb, metric]);

	useEffect(() => {
		setCB(editRuleData ? cbKey : getCB(metricDomain));
		setLocation(editRuleData ? editLocation?.name : 'USA');
		setChannelOptions(editRuleData ? editRuleData?.channels : []);
		setStateOptions(editRuleData ? editRuleData?.locationFilters : []);
	}, [metric]);

	const getChannelBreakdown = () => {
		if (metric === GMV) {
			return ['Omni', 'eComm'];
		} else {
			return [NETSALES, UNITS].includes(metric)
				? ['Omni', 'Store', 'eComm']
				: ['Store'];
		}
	};

	const cbArr = getChannelBreakdown();

	const handleLocationChange = (event) => {
		setLocation(event.target.value);
	};

	const getStoreSelection = (list) => {
		setStoreList(list);
	};

	const handleChange = (event) => {
		const {
			target: { value },
		} = event;
		setChannelOptions(
			// On autofill we get a stringified value.
			typeof value === 'string' ? value.split(',') : value
		);
	};

	const handleStateChange = (event) => {
		const {
			target: { value },
		} = event;
		setStateOptions(typeof value === 'string' ? value?.split(',') : value);
	};

	const handleCBChange = (event) => {
		setCB(event.target.value);
	};

	const dispalayLocationText = () => {
		if (cb !== 'Store' && channelOptions?.length > 0) {
			return `${cb}, ${channelOptions}`;
		} else if (
			metricDomain !== 'SALES' &&
			location === 'STATES' &&
			stateOptions?.length > 0
		) {
			return `${cb}, ${location}, multiple states selected`;
		} else if (
			metricDomain !== 'SALES' &&
			location === 'STORES' &&
			storeList?.selectedItems?.length
		) {
			return `${cb}, ${location},  multiple stores selected`;
		} else if (metricDomain !== 'SALES' && location === 'USA') {
			return `${cb}, ${location}`;
		} else if (
			location === 'STATES' &&
			channelOptions?.length > 0 &&
			stateOptions?.length > 0
		) {
			return `${cb}, ${location}, multiple states selected, ${channelOptions}`;
		} else if (
			location === 'STORES' &&
			channelOptions?.length > 0 &&
			storeList?.selectedItems?.length
		) {
			return `${cb}, ${location},  multiple stores selected, ${channelOptions}`;
		} else if (location === 'USA' && channelOptions?.length > 0) {
			return `${cb}, ${location},${channelOptions}`;
		}
		return NEED_TO_BE_SELECTED;
	};

	const handleStoreIconClick = () => {
		setAddStore(true);
	};

	const getChannel = () => {
		if (cb === 'Omni') {
			return OmniChannel;
		} else if (cb === 'eComm') {
			return eComChannel;
		} else {
			return StoreChannel;
		}
	};

	return (
		<>
			<Accordion
				elevation={0}
				classes={{
					root: classes.MuiAccordionroot,
				}}>
				<AccordionSummary
					expandIcon={<ExpandMoreIcon />}
					aria-controls="channelBreakdown-content"
					id="cb-header"
					style={{ borderTop: '1px solid lightgray !important' }}
					sx={{ borderTop: '1px solid lightgray !important', margin: 0 }}>
					<Box className={classes.boxStyle}>
						<Typography className={classes.menuItems}>
							Channel Breakdown
						</Typography>
						<Typography
							className={classes.menuItems}
							style={{ fontSize: 12, paddingTop: '6px' }}>
							{dispalayLocationText()}
						</Typography>
					</Box>
				</AccordionSummary>

				<AccordionDetails>
					<FormControl sx={{ ...styles.metricSelect, ...styles.outline }}>
						<InputLabel id="cb-label" sx={{ ...styles.textField }}>
							Channel Breakdown
						</InputLabel>
						<Select
							id="Channel Breakdown"
							value={cb}
							label="Channel Breakdown"
							onChange={handleCBChange}
							sx={{
								fontFamily: BOGLE_LIGHT,
								fontSize: 14,
							}}
							MenuProps={{
								sx: selectStyleMenu,
								disablePortal: true,
							}}>
							{cbArr?.map((name) => (
								<MenuItem key={name} value={name} className={classes.menuItems}>
									{name}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</AccordionDetails>
				{cb === 'Store' && (
					<FormControl sx={{ ...styles.metricSelect, ...styles.outline }}>
						<InputLabel
							id="simple-select-helper-label"
							sx={{ ...styles.textField, marginLeft: 2, marginRight: 2 }}>
							Location
						</InputLabel>
						<Select
							id="Location"
							value={location}
							label="Location"
							name="location"
							sx={{
								marginLeft: 2,
								marginRight: 2,
								fontFamily: BOGLE_LIGHT,
								fontSize: 14,
							}}
							onChange={handleLocationChange}
							MenuProps={{
								sx: selectStyleMenu,
								disablePortal: true,
							}}>
							{locationArr?.map((data) => (
								<MenuItem
									key={data.name}
									value={data.name}
									className={classes.menuItems}>
									{data.value}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				)}
				{location === 'STORES' && (
					<Tooltip
						show={storedValue?.showAddStoreTooltip}
						placement="bottom-start"
						type={'addStore'}>
						{/* Wrapped w/ Tooltip for FT UX */}
						<Box
							style={{
								marginTop: '20px',
								marginLeft: '20px',
								display: 'flex',
								alignItems: 'center',
								cursor: 'pointer',
							}}
							onClick={handleStoreIconClick}>
							{/* Hot spot for FT UX  */}
							<HotSpot
								show={storedValue?.showAddStoreTooltip}
								top={'50%'}
								left={'128%'}>
								<img alt="storeIcon" src={storeIcon} />
								<span style={{ marginLeft: '10px' }}>Add Stores</span>
							</HotSpot>
						</Box>
					</Tooltip>
				)}
				{location === 'STATES' && (
					<FormControl
						sx={{ ...styles.metricSelect, ...styles.outline }}
						style={{ marginTop: '25px' }}>
						<InputLabel
							id="simple-select-helper-label"
							sx={{ ...styles.textField, marginLeft: 2, marginRight: 2 }}>
							State
						</InputLabel>
						<Select
							labelId="demo-multiple-checkbox-label"
							id="demo-multiple-checkbox"
							multiple
							value={stateOptions}
							onChange={handleStateChange}
							input={<OutlinedInput label="State" />}
							renderValue={(selected) => selected.join(', ')}
							sx={{
								marginLeft: 2,
								marginRight: 2,
								fontFamily: BOGLE_LIGHT,
								fontSize: 14,
							}}
							MenuProps={{
								sx: selectStyleMenu,
								disablePortal: true,
							}}>
							{states?.map((data) => (
								<MenuItem key={data.name} value={data.name}>
									<Checkbox checked={stateOptions.indexOf(data.name) > -1} />
									<ListItemText primary={data.value} />
								</MenuItem>
							))}
						</Select>
					</FormControl>
				)}
				{cb !== NEED_TO_BE_SELECTED && [NETSALES, UNITS, GMV].includes(metric) && (
					<FormControl
						sx={{ ...styles.metricSelect, ...styles.outline }}
						style={{ marginTop: '25px' }}>
						<InputLabel
							id="simple-select-helper-label"
							sx={{ ...styles.textField, marginLeft: 2, marginRight: 2 }}>
							Channel option
						</InputLabel>

						<Select
							labelId="demo-multiple-checkbox-label"
							id="demo-multiple-checkbox"
							multiple
							value={channelOptions}
							onChange={handleChange}
							input={<OutlinedInput label="Channel option" />}
							renderValue={(selected) => selected.join(', ')}
							sx={{
								marginLeft: 2,
								marginRight: 2,
								fontFamily: BOGLE_LIGHT,
								fontSize: 14,
								'& .MuiButtonBase-root': {
									padding: '5px !important',
								},
								'& .MuiButtonBase-root > span': {
									width: '10% !important',
									borderTop: '1px solid white !important',
								},
								'& .dialog-title h2': {
									display: 'flex !important',
								},
							}}
							MenuProps={{
								sx: selectStyleMenu,
								disablePortal: true,
							}}>
							{getChannel()?.map((data) => (
								<MenuItem
									key={data.name}
									value={data.name}
									style={{ display: 'inline-flex !important' }}
									sx={{ display: 'inline-flex !important' }}>
									<Checkbox checked={channelOptions.indexOf(data.name) > -1} />
									<ListItemText primary={data.value} />
								</MenuItem>
							))}
						</Select>
					</FormControl>
				)}
			</Accordion>

			<AddItem
				isOpen={addStore ? true : false}
				type={'Store'}
				setIsAddItemsOpen={setAddStore}
				getData={getStoreSelection}
			/>
		</>
	);
};

Location.propTypes = {
	metric: PropTypes.string,
	metricDomain: PropTypes.string,
	editRuleData: PropTypes.object,
	getLocationData: PropTypes.func,
};

export default Location;
